import React, { useState, useRef, useEffect } from "react";
import { EditorObject } from "../types/editorTypes";
import { PencilColorIcon, StrokeWidthIcon } from "../../SVGIcons";

interface SelectedMarkerOptionsProps {
  selectedMarkerObject: EditorObject;
  updateMarkerProperty: (property: keyof EditorObject, value: any) => void;
}

const STROKE_WIDTH_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '1px' },
  { value: 2, label: '2px' },
  { value: 3, label: '3px' },
  { value: 4, label: '4px' },
  { value: 8, label: '8px' },
  { value: 16, label: '16px' },
  { value: 32, label: '32px' },
];

export const SelectedMarkerOptions: React.FC<SelectedMarkerOptionsProps> = ({
  selectedMarkerObject,
  updateMarkerProperty,
}) => {
  const [showWidthDropdown, setShowWidthDropdown] = useState(false);
  const widthDropdownRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (widthDropdownRef.current && !widthDropdownRef.current.contains(e.target as Node)) {
        setShowWidthDropdown(false);
      }
    };
    if (showWidthDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWidthDropdown]);

  const currentStrokeWidth = selectedMarkerObject.strokeWidth || 8;
  const currentWidthLabel = STROKE_WIDTH_OPTIONS.find(opt => opt.value === currentStrokeWidth)?.label || '8px';
  const currentStrokeColor = selectedMarkerObject.stroke || '#ffff00';

  return (
    <>
      {/* 線の色 */}
      <div className="relative flex items-center">
        <button
          onClick={() => colorInputRef.current?.click()}
          className="p-1.5 rounded hover:bg-gray-100 flex items-center"
          title="線の色"
        >
          <PencilColorIcon className="w-5 h-5" barColor={currentStrokeColor} />
        </button>
        <input
          ref={colorInputRef}
          type="color"
          value={currentStrokeColor}
          onChange={(e) => updateMarkerProperty('stroke', e.target.value)}
          style={{ position: 'absolute', top: '100%', left: 0, visibility: 'hidden' }}
        />
      </div>

      {/* 線の太さ */}
      <div className="relative" ref={widthDropdownRef}>
        <button
          onClick={() => setShowWidthDropdown(!showWidthDropdown)}
          className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1"
          title="線の太さ"
        >
          <StrokeWidthIcon className="w-5 h-5" />
          <span className="text-xs text-gray-600">{currentWidthLabel}</span>
        </button>
        {showWidthDropdown && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[80px]">
            {STROKE_WIDTH_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  updateMarkerProperty('strokeWidth', option.value);
                  setShowWidthDropdown(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                  currentStrokeWidth === option.value ? 'bg-orange-100 text-orange-600' : ''
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SelectedMarkerOptions;
